import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Col, Row, Button, Modal, Spinner, Card, Accordion, Alert } from 'react-bootstrap';
import { ReactComponent as DeleteIcon } from '../../images/trash3-fill.svg';
import { ReactComponent as EditIcon } from '../../images/edit.svg';
import { ReactComponent as PlayIcon } from '../../images/play-fill.svg';
import { ReactComponent as DescriptionIcon } from '../../images/portfolio.svg';
import { ReactComponent as UploadIcon } from '../../images/box-arrow-in-up.svg';
import { ReactComponent as ViewIcon } from '../../images/eye.svg';
import './UserProfile.css';
import '../Preview/Preview.css';

import { API_ROUTES, BASE_URL } from '../../config/api-routes';
import { PromptVisibilityIndicator } from '../Prompts/PromptVisibilityIndicator';
import { mediaUrl, normalizeAppPrompt } from '../Prompts/promptsV2Utils';
import { resolvePromptVisibility } from '../../utils/promptVisibilityMeta';
import type { AppPrompt } from '../Prompts/promptsV2Types';
import PromptEditModal from '../Prompts/PromptEditModal';
import axiosInstance from '../../api/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export interface Prompt {
  id: number;
  created_at: Date;
  updated_at: Date;
  description?: string;
  content: string;
  fileUrl: string;
  supportedDocumentUrl?: string;
  visibility?: string;
}

interface promptsTabProps {
  prompts: Prompt[];
  setPromptsData: Dispatch<SetStateAction<any>>;
}

function promptHasVideo(prompt: Prompt) {
  const url = String(prompt.fileUrl ?? '').trim();
  return Boolean(url && url !== 'N/A');
}

function PromptStatusChips({ prompt }: { prompt: Prompt }) {
  const hasVideo = promptHasVideo(prompt);
  const hasDoc = Boolean(prompt.supportedDocumentUrl?.trim());
  const hasDesc = Boolean(prompt.description?.trim());
  const doneCount = [hasVideo, hasDesc].filter(Boolean).length;
  const isComplete = doneCount === 2;

  const renderItem = (label: string, done: boolean) => (
    <span
      className={`prompt-status-item ${done ? 'is-done' : 'is-missing'}`}
      aria-label={`${label}${done ? ' added' : ' missing'}`}
    >
      <span className="prompt-status-mark" aria-hidden>
        {done ? '✓' : '–'}
      </span>
      <span className="prompt-status-label">{label}</span>
    </span>
  );

  return (
    <div
      className="prompt-tab-status-row"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      role="presentation"
    >
      {renderItem('Video', hasVideo)}
      <span className="prompt-status-sep" aria-hidden>
        ·
      </span>
      {renderItem('Document', hasDoc)}
      <span className="prompt-status-sep" aria-hidden>
        ·
      </span>
      {renderItem('Description', hasDesc)}
      <span className={`prompt-status-summary ${isComplete ? 'is-complete' : ''}`}>
        {isComplete ? 'Complete' : `${doneCount}/3`}
      </span>
    </div>
  );
}

const PromptsTabContent: React.FC<promptsTabProps> = ({ prompts, setPromptsData }) => {
  const [loader, setLoader] = useState<boolean>(false);
  const [editingPrompt, setEditingPrompt] = useState<AppPrompt | null>(null);
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [dismissedUpgradeCards, setDismissedUpgradeCards] = useState<{
    [key: number]: boolean;
  }>({});
  const MIN_PROMPTS = 4;
  const MAX_PROMPTS = 6;

  const toAppPrompt = (item: Prompt): AppPrompt =>
    normalizeAppPrompt(item as unknown as Record<string, unknown>);

  const openEditModal = (item: Prompt) => {
    setEditingPrompt(toAppPrompt(item));
  };

  const mergePromptFromApi = (existing: Prompt, apiData: Record<string, unknown>): Prompt => {
    const normalized = normalizeAppPrompt({
      ...existing,
      ...apiData,
      id: existing.id,
    });
    return {
      ...existing,
      content: normalized.content || existing.content,
      description: normalized.description ?? existing.description,
      fileUrl: normalized.fileUrl || existing.fileUrl || 'N/A',
      supportedDocumentUrl: normalized.supportedDocumentUrl ?? existing.supportedDocumentUrl,
      visibility: normalized.visibility ?? existing.visibility,
    };
  };

  const refreshPromptsList = async (focusId?: number) => {
    try {
      const res = await axiosInstance.get(API_ROUTES.PROMPTS.GET_PROMPTS);
      if (!Array.isArray(res.data)) return;
      setPromptsData(res.data);
      if (focusId != null) {
        const fresh = res.data.find((p: { id: number }) => p.id === focusId);
        if (fresh) {
          setPrompt((prev) =>
            prev?.id === focusId ? mergePromptFromApi(prev, fresh as Record<string, unknown>) : prev
          );
        }
      }
      window.dispatchEvent(new Event('prompts-updated'));
    } catch (err) {
      console.error('Failed to refresh prompts', err);
    }
  };

  useEffect(() => {
    const focusId = prompt?.id;
    if (!focusId) return;
    const fresh = prompts.find((p) => p.id === focusId);
    if (!fresh) return;
    setPrompt((prev) => {
      if (!prev || prev.id !== focusId) return prev;
      if (
        fresh.description === prev.description &&
        fresh.fileUrl === prev.fileUrl &&
        fresh.supportedDocumentUrl === prev.supportedDocumentUrl
      ) {
        return prev;
      }
      return mergePromptFromApi(prev, fresh as unknown as Record<string, unknown>);
    });
  }, [prompts, prompt?.id]);

  useEffect(() => {
    if (!showVideoPlayer || !prompt?.fileUrl || prompt.fileUrl === 'N/A') {
      return;
    }
    setSelectedVideoUrl(mediaUrl(prompt.fileUrl) || prompt.fileUrl);
  }, [showVideoPlayer, prompt?.fileUrl, prompt?.id]);

  const onVideoModalClose = () => {
    setShowVideoPlayer(false);
    setSelectedVideoUrl(null);
  };

  const onDeleteModalClose = () => {
    setShowRemoveModal(false);
    setPrompt(null);
  };

  const handlePromptSaved = (updated: AppPrompt) => {
    const merged = mergePromptFromApi(
      prompts.find((p) => p.id === updated.id) ?? (updated as unknown as Prompt),
      updated as unknown as Record<string, unknown>
    );
    setPromptsData((prev: Prompt[]) =>
      prev.map((item) => (item.id === updated.id ? merged : item))
    );
    setPrompt((prev) => (prev?.id === updated.id ? merged : prev));
    void refreshPromptsList(updated.id);
  };

  const handleDeletePrompt = async () => {
    if (isDeleting || !prompt) return;

    if (prompts.length <= MIN_PROMPTS) {
      toast.error(`You must keep at least ${MIN_PROMPTS} prompts`);
      onDeleteModalClose();
      return;
    }

    const willBe = prompts.length - 1;
    if (willBe === MIN_PROMPTS) {
      const confirm = window.confirm(
        `This will leave you with ${MIN_PROMPTS} prompts (the minimum). Continue?`
      );
      if (!confirm) {
        onDeleteModalClose();
        return;
      }
    }

    const id = prompt.id; // string or number; send as-is
    setIsDeleting(true);
    try {
      setLoader(true);
      const res = await axiosInstance.delete(
        `${BASE_URL}${API_ROUTES.PROMPTS.DELETE_PROMPT}/${id}`
      );
      if (!res || ![200, 204].includes(res.status)) {
        throw new Error(`Unexpected status ${res?.status}`);
      }

      setPromptsData((prev: Prompt[]) => {
        const updatedData = [...prev];
        const promptIndex = updatedData.findIndex((item) => item.id === prompt?.id);
        if (promptIndex > -1) updatedData.splice(promptIndex, 1);
        return updatedData;
      });
      window.dispatchEvent(new Event('prompts-updated'));
      toast.success('Screening Prompt deleted');
    } catch (err: any) {
      console.error('Delete prompt failed', err?.response?.status, err);
      if (err?.response?.status === 404) {
        toast.error('Prompt not found on server (404). Check ID/ownership.');
      } else {
        toast.error('Failed to delete prompt');
      }
    } finally {
      setIsDeleting(false);
      onDeleteModalClose();
      setLoader(false);
    }
  };

  const viewVideo = (videoFile: string, selectedPrompt: Prompt) => {
    setSelectedVideoUrl(mediaUrl(videoFile) || videoFile);
    setPrompt(selectedPrompt);
    setShowVideoPlayer(true);
  };

  const requestDeletePrompt = (item: Prompt) => {
    setEditingPrompt(null);
    setPrompt(item);
    setShowRemoveModal(true);
  };

  return (
    <>
      <Card className="references-card-modern profile-prompts-card-modern mb-3">
        <Card.Header className="preview-cards-ref-header references-header">
          <span className="references-title">My Screening Prompts</span>
        </Card.Header>
        <Card.Body className="references-body profile-prompts-body">
          <Alert
            variant={
              prompts.length < MIN_PROMPTS
                ? 'danger'
                : prompts.length >= MAX_PROMPTS
                  ? 'warning'
                  : 'info'
            }
            className="mb-3 profile-prompts-count-alert"
          >
            <strong>{prompts.length}</strong> of {MAX_PROMPTS} prompts.
            {prompts.length < MIN_PROMPTS && ' You must have at least 4 prompts.'}
            {prompts.length === MIN_PROMPTS && ' You are at the minimum (4).'}
            {prompts.length >= MAX_PROMPTS && ' You’ve reached the maximum (6).'}
          </Alert>

          <Accordion alwaysOpen className="profile-prompts-accordion">
            {prompts.map((prompt) => (
              <Accordion.Item
                eventKey={String(prompt.id)}
                key={prompt.id}
                className={`accordion-item-mod prompt-tab-accordion-mod${!promptHasVideo(prompt) ? ' missing-video' : ''}`}
              >
                <Accordion.Header className="accordion-header-mod">
                  <div className="prompt-tab-header-mod">
                    <div className="prompt-tab-header-main">
                      <p className="prompt-tab-text-mod">{prompt.content}</p>
                      <PromptStatusChips prompt={prompt} />
                      <div className="prompt-tab-privacy-row">
                        <PromptVisibilityIndicator
                          visibility={resolvePromptVisibility(prompt.visibility)}
                          itemLabel="screening prompt"
                        />
                      </div>
                    </div>

                    <div
                      className="prompt-tab-default-buttons"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      role="presentation"
                    >
                      <Button
                        className="prompt-tab-button-mod prompt-tab-button-mod--edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(prompt);
                        }}
                        title="Edit prompt"
                      >
                        <EditIcon style={{ width: '15px', height: '15px' }} />
                      </Button>
                      {prompts.length > MIN_PROMPTS && (
                        <Button
                          className="prompt-tab-button-mod delete-btn"
                          variant="danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!prompt.id) {
                              toast.error('Invalid prompt ID — cannot delete');
                              return;
                            }
                            setPrompt(prompt);
                            setShowRemoveModal(true);
                          }}
                          title="Delete prompt"
                          disabled={isDeleting}
                        >
                          <DeleteIcon style={{ width: '15px', height: '15px' }} />
                        </Button>
                      )}
                    </div>
                  </div>
                </Accordion.Header>

                <Accordion.Body className="accordion-body-mod">
                  <div className="prompt-tab-expanded-section">
                    {!promptHasVideo(prompt) && (
                      <div className="prompt-no-video-alert">
                        <span className="prompt-no-video-icon">🎥</span>
                        <div className="prompt-no-video-text">
                          <strong>No video added yet.</strong> Record or upload a video to complete
                          this prompt!
                        </div>
                        <Button
                          size="sm"
                          className="prompt-no-video-btn"
                          onClick={() => openEditModal(prompt)}
                        >
                          📹 Add Video
                        </Button>
                      </div>
                    )}
                    <div className="divider" />
                    <div className="prompt-tab-expanded-buttons-mod">
                      {promptHasVideo(prompt) && (
                        <Button
                          className="prompt-tab-button-mod prompt-tab-action-btn prompt-tab-action-btn--watch"
                          onClick={() => viewVideo(prompt.fileUrl, prompt)}
                        >
                          <PlayIcon width={18} height={18} /> Watch Prompt
                        </Button>
                      )}

                      {prompt.supportedDocumentUrl ? (
                        <Button
                          className="prompt-tab-button-mod prompt-tab-action-btn prompt-tab-action-btn--view"
                          onClick={() =>
                            window.open(
                              mediaUrl(prompt.supportedDocumentUrl),
                              '_blank',
                              'noopener,noreferrer'
                            )
                          }
                        >
                          <DescriptionIcon width={18} height={18} /> View Document
                        </Button>
                      ) : (
                        <Button
                          className="prompt-tab-button-mod prompt-tab-action-btn prompt-tab-action-btn--upload"
                          onClick={() => openEditModal(prompt)}
                        >
                          <UploadIcon width={18} height={18} /> Upload Document
                        </Button>
                      )}

                      {prompt.description ? (
                        <Button
                          className="prompt-tab-button-mod prompt-tab-action-btn prompt-tab-action-btn--view"
                          onClick={() => openEditModal(prompt)}
                        >
                          <ViewIcon width={18} height={18} /> View Description
                        </Button>
                      ) : (
                        <Button
                          className="prompt-tab-button-mod prompt-tab-action-btn prompt-tab-action-btn--description"
                          onClick={() => openEditModal(prompt)}
                        >
                          <EditIcon width={18} height={18} /> Write Description
                        </Button>
                      )}
                    </div>
                    {promptHasVideo(prompt) &&
                      (!prompt.description || !prompt.supportedDocumentUrl) &&
                      !dismissedUpgradeCards[prompt.id] && (
                        <div className="prompt-upgrade-card mt-3">
                          <button
                            type="button"
                            className="prompt-upgrade-close"
                            aria-label="Dismiss enhancement card"
                            onClick={() =>
                              setDismissedUpgradeCards((prev) => ({
                                ...prev,
                                [prompt.id]: true,
                              }))
                            }
                          >
                            ×
                          </button>

                          <h6 className="prompt-upgrade-title mb-2">
                            Improve this prompt response
                          </h6>
                          <p className="prompt-upgrade-copy mb-3">
                            {prompt.description
                              ? 'Add a supporting document so this response has stronger evidence.'
                              : prompt.supportedDocumentUrl
                                ? 'Add a short description to provide context for your video response.'
                                : 'Add both a short description and a supporting document to make this prompt more compelling.'}
                          </p>

                          <div className="d-flex gap-2 flex-wrap">
                            {!prompt.description && (
                              <Button
                                size="sm"
                                variant="success"
                                onClick={() => {
                                  openEditModal(prompt);
                                  setDismissedUpgradeCards((prev) => ({
                                    ...prev,
                                    [prompt.id]: false,
                                  }));
                                }}
                              >
                                Add Description
                              </Button>
                            )}

                            {!prompt.supportedDocumentUrl && (
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => {
                                  openEditModal(prompt);
                                  setDismissedUpgradeCards((prev) => ({
                                    ...prev,
                                    [prompt.id]: false,
                                  }));
                                }}
                              >
                                Attach Document
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Card.Body>
      </Card>

      <PromptEditModal
        prompt={editingPrompt}
        onClose={() => setEditingPrompt(null)}
        onSaved={handlePromptSaved}
        onDeleteRequest={(promptId) => {
          const item = prompts.find((p) => p.id === promptId);
          if (item) requestDeletePrompt(item);
        }}
        showDelete={prompts.length > MIN_PROMPTS}
      />

      <Modal
        show={showRemoveModal}
        onHide={onDeleteModalClose}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Body style={{ overflowY: 'auto' }} className="prompt-modal-container p-5">
          <div>
            <Row sm={12}>
              <h5>Are you sure you want to delete the prompt?</h5>

              <Col
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '10px',
                }}
              >
                <Button
                  variant="secondary"
                  className="modal-sub-title"
                  onClick={onDeleteModalClose}
                >
                  Cancel
                </Button>
                <Button variant="danger" className="modal-sub-title" onClick={handleDeletePrompt}>
                  {loader ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Saving...
                    </>
                  ) : (
                    'Delete'
                  )}
                </Button>
              </Col>
            </Row>
          </div>
        </Modal.Body>
      </Modal>

      {selectedVideoUrl && prompt && (
        <Modal
          show={showVideoPlayer}
          onHide={onVideoModalClose}
          centered
          backdrop="static"
          keyboard={false}
          size="xl"
          dialogClassName="prompt-player-modal"
          contentClassName="prompt-player-modal__content"
        >
          <Modal.Header closeButton className="prompt-player-modal__header">
            <Modal.Title>Prompt response</Modal.Title>
          </Modal.Header>

          <Modal.Body className="prompt-player-modal__body">
            <Row className="g-4">
              <Col lg={7} md={12}>
                <div className="prompt-player-video-wrap">
                  <video
                    key={selectedVideoUrl}
                    controls
                    playsInline
                    className="prompt-player-video"
                  >
                    <source src={selectedVideoUrl} type="video/mp4" />
                  </video>
                </div>
              </Col>

              <Col lg={5} md={12}>
                <Card className="prompt-player-details h-100 border-0 shadow-sm">
                  <Card.Body className="d-flex flex-column">
                    <p className="prompt-player-label mb-1">Prompt</p>
                    <h5 className="prompt-player-question">
                      {prompt.content ?? 'Untitled prompt'}
                    </h5>

                    <hr className="my-3" />

                    <p className="prompt-player-label mb-2">Description</p>
                    <div className="prompt-player-description flex-grow-1">
                      {prompt.description?.trim() ? (
                        prompt.description
                      ) : (
                        <span className="text-muted">No description provided.</span>
                      )}
                    </div>

                    <div className="prompt-player-actions">
                      {prompt.supportedDocumentUrl ? (
                        <button
                          type="button"
                          className="prompt-player-btn prompt-player-btn--doc"
                          onClick={() =>
                            window.open(
                              mediaUrl(prompt.supportedDocumentUrl),
                              '_blank',
                              'noopener,noreferrer'
                            )
                          }
                        >
                          View supporting document
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="prompt-player-btn prompt-player-btn--edit"
                        onClick={() => {
                          setShowVideoPlayer(false);
                          openEditModal(prompt);
                        }}
                      >
                        Edit response
                      </button>
                      <button
                        type="button"
                        className="prompt-player-btn prompt-player-btn--replace"
                        onClick={() => {
                          setShowVideoPlayer(false);
                          openEditModal(prompt);
                        }}
                      >
                        Replace video
                      </button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer className="prompt-player-modal__footer">
            <button
              type="button"
              className="prompt-player-btn prompt-player-btn--close"
              onClick={onVideoModalClose}
            >
              Close
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default PromptsTabContent;
